import {
  BreadcrumbHeading,
  ChannelIcon,
  KpiCardLarge,
  Spinner,
  StarCircled,
  UnlockedIcon,
  VenueIcon,
  type KpiCardLargeProps,
} from '@weareopenr/component-library';
import { usePermissions, useGetBrands, useGetChannelsAddons, useGetUsers } from 'src/hooks';
import { IBrandResponse } from 'src/api/getBrands/types';
import { BrandsNeedingAttention } from 'src/components/BrandsNeedingAttention/BrandsNeedingAttention';
import { ChannelAdoption } from 'src/components/ChannelAdoption/ChannelAdoption';
import { RecentUsers } from 'src/components/RecentUsers/RecentUsers';
import styles from './Dashboard.module.scss';

const sumBy = (brands: IBrandResponse[], getValue: (brand: IBrandResponse) => number): number =>
  brands.reduce((total, brand) => total + getValue(brand), 0);

export const Dashboard = () => {
  const { canViewUsers } = usePermissions();

  const { data: brands = [], isLoading: brandsLoading } = useGetBrands();
  const { data: addons = [], isInitialLoading: addonsLoading } = useGetChannelsAddons();
  const { data: usersData, isInitialLoading: usersLoading } = useGetUsers(canViewUsers);

  const users = usersData?.data ?? [];

  if (brandsLoading || addonsLoading || usersLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );
  }

  const activeUsers = users.filter((user) => user.state === 'active').length;

  const kpiCards: KpiCardLargeProps[] = [
    {
      id: 'brands',
      size: 'large',
      testId: 'kpi-brands',
      value: brands.length,
      label: 'Brands',
      icon: StarCircled,
      to: '/brands',
      linkLabel: 'View brands',
    },
    {
      id: 'venues',
      size: 'large',
      testId: 'kpi-venues',
      value: sumBy(brands, (brand) => brand.venues),
      label: 'Venues',
      icon: VenueIcon,
      caption: 'Across all brands',
    },
    {
      id: 'extensions',
      size: 'large',
      testId: 'kpi-extensions',
      value: sumBy(brands, (brand) => brand.configured_addons.length),
      label: 'Configured extensions',
      icon: ChannelIcon,
      caption: 'Live integrations',
    },
    ...(canViewUsers
      ? [
          {
            id: 'users',
            size: 'large',
            testId: 'kpi-users',
            value: activeUsers,
            label: 'Active users',
            icon: UnlockedIcon,
            to: '/users',
            linkLabel: 'View users',
          } as KpiCardLargeProps,
        ]
      : []),
  ];

  return (
    <div className={styles.container}>
      <BreadcrumbHeading breadcrumbs={[{ label: 'Dashboard', path: '/' }]} />

      <div className={styles['kpi-grid']}>
        {kpiCards.map((card) => (
          <KpiCardLarge key={card.id} {...card} />
        ))}
      </div>

      <div className={styles.grid}>
        <BrandsNeedingAttention brands={brands} />
        <ChannelAdoption brands={brands} addons={addons} />
      </div>

      {canViewUsers && <RecentUsers users={users} />}
    </div>
  );
};
